import { z } from 'zod';

// Mirrors the TOMS external API contract documented at
// docs/CO_ORDER_API.md. The shape MUST match TOMS exactly for the
// backend proxy to pass it through unchanged.

export const coOrderTypeEnum = z.enum([
  'ROUTINE_ORDER',
  'PART_TWO_ORDER',
  'TRAINING_INSTRUCTION',
  'SPECIAL_ASSIGNMENT',
  'ADMINISTRATIVE_ORDER',
]);
export type CoOrderType = z.infer<typeof coOrderTypeEnum>;

export const coOrderPriorityEnum = z.enum(['URGENT', 'HIGH', 'NORMAL', 'LOW']);
export type CoOrderPriority = z.infer<typeof coOrderPriorityEnum>;

export const coOrderStatusEnum = z.enum(['SENT', 'FAILED']);
export type CoOrderStatus = z.infer<typeof coOrderStatusEnum>;

/** Display labels for each `type` value. Used in the UI dropdown + history. */
export const CO_ORDER_TYPE_DISPLAY: Record<CoOrderType, string> = {
  ROUTINE_ORDER: 'Routine Order',
  PART_TWO_ORDER: 'Part II Order',
  TRAINING_INSTRUCTION: 'Training Instruction',
  SPECIAL_ASSIGNMENT: 'Special Assignment',
  ADMINISTRATIVE_ORDER: 'Administrative Order',
};

/**
 * Body the CO submits from our UI. Matches the TOMS API contract verbatim
 * so the backend can forward it unchanged.
 *
 * Note: TOMS caps `body` at ~280 displayed chars on the barrack kiosk but
 * accepts longer text. We cap server-side at 2000 to keep the audit row
 * sensible — the kiosk truncates anything beyond ~280 anyway.
 */
export const broadcastCoOrderRequestSchema = z.object({
  title: z
    .string()
    .min(3, 'Title must be at least 3 characters')
    .max(200, 'Title must be 200 characters or fewer'),
  body: z.string().min(1, 'Body is required').max(2000, 'Body must be 2000 characters or fewer'),
  priority: coOrderPriorityEnum.default('URGENT'),
  type: coOrderTypeEnum.default('ROUTINE_ORDER'),
});
export type BroadcastCoOrderRequest = z.infer<typeof broadcastCoOrderRequestSchema>;

/** A single row from our local history table. */
export const coOrderSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  body: z.string(),
  type: coOrderTypeEnum,
  priority: coOrderPriorityEnum,
  status: coOrderStatusEnum,
  tomsId: z.string().nullable(),
  tomsOrderNumber: z.string().nullable(),
  tomsUrl: z.string().nullable(),
  publishedAt: z.string().nullable(),
  errorMessage: z.string().nullable(),
  sentByUserId: z.string().uuid(),
  sentByName: z.string().nullable(),
  sentAt: z.string(),
});
export type CoOrder = z.infer<typeof coOrderSchema>;

export const listCoOrdersQuerySchema = z.object({
  page: z.coerce.number().int().positive().optional(),
  pageSize: z.coerce.number().int().positive().max(100).optional(),
});
export type ListCoOrdersQuery = z.infer<typeof listCoOrdersQuerySchema>;

export interface ListCoOrdersResponse {
  orders: CoOrder[];
  page: number;
  pageSize: number;
  total: number;
}

export interface CoOrdersStatus {
  configured: boolean;
}
